import { useEffect, useMemo, useRef, useState } from "react";
import { Search, ArrowUpRight } from "lucide-react";

const SECTIONS = [
  // Workspace
  { label: "My Dashboard", view: "dashboard", group: "Workspace", keywords: "home workspace overview monitoring daycare", roles: ["*"] },
  { label: "Care Connections", view: "care", group: "Workspace", keywords: "caregivers babysitter nanny find care marketplace messages", roles: ["*"] },
  { label: "Store", view: "store", group: "Workspace", keywords: "shop products cart checkout ecommerce buy", roles: ["*"] },
  { label: "Provider Directory", view: "providers", group: "Workspace", keywords: "state programs providers directory listings", roles: ["*"] },
  { label: "Secure Inbox", view: "admin-inbox", group: "Workspace", keywords: "secure messages admin threads notes escalations pin", roles: ["master", "daycare_admin"] },
  { label: "Facility Dashboard", view: "facility-dash", group: "Workspace", keywords: "facility baby dashboard owner staff parents enrollment", roles: ["daycare_admin", "master"] },
  // Public site
  { label: "Home / Landing Page", view: "landing", group: "Site", keywords: "home landing homepage public", roles: ["*"] },
  { label: "About", view: "about", group: "Site", keywords: "about company mission", roles: ["*"] },
  { label: "Contact", view: "contact", group: "Site", keywords: "support contact email help", roles: ["*"] },
  { label: "Terms & Conditions", view: "terms", group: "Site", keywords: "legal terms conditions", roles: ["*"] },
  { label: "Privacy Policy", view: "privacy", group: "Site", keywords: "legal privacy policy", roles: ["*"] },
  // Master Control panels (anchor = element id / testid on the master dashboard)
  { label: "Master Control", view: "master", group: "Master Control", keywords: "master admin control panel overview", roles: ["master"] },
  { label: "Platform Stats", view: "master", anchor: "master-stats", group: "Master Control", keywords: "overview statistics numbers stats", roles: ["master"] },
  { label: "Go-Live Checklist", view: "master", anchor: "sec-golive", group: "Master Control", keywords: "launch readiness go live checklist", roles: ["master"] },
  { label: "User Directory", view: "master", anchor: "user-directory", group: "Master Control", keywords: "users accounts suspend reactivate moderation contacts", roles: ["master"] },
  { label: "Invite People", view: "master", anchor: "master-invite-panel", group: "Master Control", keywords: "invite master staff caregiver parent role", roles: ["master"] },
  { label: "Master Staff", view: "master", anchor: "master-staff-panel", group: "Master Control", keywords: "staff positions team permissions", roles: ["master"] },
  { label: "Applications & Enrollment", view: "master", anchor: "sec-onboarding", group: "Master Control", keywords: "applications onboarding enrollment review", roles: ["master"] },
  { label: "Hierarchy & Positions", view: "master", anchor: "sec-hierarchy", group: "Master Control", keywords: "hierarchy positions job titles permissions", roles: ["master"] },
  { label: "Support Inquiries", view: "master", anchor: "sec-support", group: "Master Control", keywords: "support inquiries tickets help", roles: ["master"] },
  { label: "Platform Alerts", view: "master", anchor: "sec-alerts", group: "Master Control", keywords: "alerts notifications review queue", roles: ["master"] },
  { label: "Complaints", view: "master", anchor: "sec-complaints", group: "Master Control", keywords: "complaints safety escalation reports", roles: ["master"] },
  { label: "Broadcast Center", view: "master", anchor: "sec-broadcast", group: "Master Control", keywords: "broadcast announcements email sms audience", roles: ["master"] },
  { label: "Communications Log", view: "master", anchor: "sec-commlog", group: "Master Control", keywords: "communications log emails sms history", roles: ["master"] },
  { label: "Store Manager", view: "master", anchor: "sec-store", group: "Master Control", keywords: "store manager products inventory prices promos", roles: ["master"] },
  { label: "Sponsor Ads", view: "master", anchor: "sec-ads", group: "Master Control", keywords: "sponsor ads advertising revenue rate card placements", roles: ["master"] },
  { label: "Featured Daycares", view: "master", anchor: "featured-panel", group: "Master Control", keywords: "featured daycares listings requests analytics", roles: ["master"] },
  { label: "Site Content & SEO", view: "master", anchor: "site-content-panel", group: "Master Control", keywords: "site content seo meta copy edit text headings", roles: ["master"] },
  { label: "Analytics & Activity", view: "master", anchor: "sec-analytics", group: "Master Control", keywords: "analytics activity traffic visitors global search", roles: ["master"] },
];

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const SiteSearch = ({ currentUser, onNavigate, placeholder = "Search the whole site…", className = "", testid = "site-search" }) => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const wrapRef = useRef(null);

  const userRoles = useMemo(() => [currentUser?.role, ...(currentUser?.extra_roles || [])].filter(Boolean), [currentUser]);
  const visible = useMemo(
    () => SECTIONS.filter((s) => s.roles.includes("*") || s.roles.some((r) => userRoles.includes(r))),
    [userRoles]
  );
  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    return visible.filter((s) => `${s.label} ${s.group} ${s.keywords}`.toLowerCase().includes(needle)).slice(0, 8);
  }, [q, visible]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const pick = (s) => {
    setQ("");
    setOpen(false);
    onNavigate?.(s);
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`} data-testid={testid}>
      <Search className="w-3.5 h-3.5 text-[#9CA3AF] absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
      <input
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
          setHi(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setOpen(false);
            e.target.blur();
          } else if (e.key === "ArrowDown" && matches.length) {
            e.preventDefault();
            setHi((h) => (h + 1) % matches.length);
          } else if (e.key === "ArrowUp" && matches.length) {
            e.preventDefault();
            setHi((h) => (h - 1 + matches.length) % matches.length);
          } else if (e.key === "Enter" && matches.length) {
            e.preventDefault();
            pick(matches[hi] || matches[0]);
          }
        }}
        placeholder={placeholder}
        className="w-full bg-[#FFF9F2] border border-[#FCE6E9] rounded-full pl-9 pr-3.5 py-2 text-xs font-semibold outline-none focus:border-[#E5A0BC] placeholder:text-[#C9B7C0] text-[#374151] dark:bg-[#273244] dark:border-[#374457] dark:text-slate-200"
        data-testid={`${testid}-input`}
      />
      {open && q.trim() && (
        <div
          className="absolute left-0 right-0 top-full mt-2 bg-white border border-[#FCE6E9] rounded-2xl shadow-xl p-1.5 z-[60] dark:bg-[#273244] dark:border-[#374457]"
          data-testid={`${testid}-results`}
        >
          {matches.map((s, i) => (
            <button
              key={`${s.view}-${s.anchor || "top"}-${s.label}`}
              onMouseEnter={() => setHi(i)}
              onClick={() => pick(s)}
              data-testid={`${testid}-result-${slugify(s.label)}`}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-left text-xs font-bold text-[#374151] dark:text-slate-200 transition-colors ${
                i === hi ? "bg-[#FCE6E9] dark:bg-[#374457]" : "hover:bg-[#FFF4F7] dark:hover:bg-[#2f3b50]"
              }`}
            >
              <span className="flex items-center gap-2 min-w-0">
                <ArrowUpRight className="w-3.5 h-3.5 text-[#E5A0BC] shrink-0" />
                <span className="truncate">{s.label}</span>
              </span>
              <span className="text-[9px] font-extrabold text-[#9CA3AF] uppercase shrink-0">{s.group}</span>
            </button>
          ))}
          {matches.length === 0 && (
            <p className="text-[10px] text-[#9CA3AF] font-semibold px-3 py-2" data-testid={`${testid}-empty`}>
              No sections match — try "store", "ads", "inbox", "directory"…
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default SiteSearch;
